import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Modal, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";

import Papa from "papaparse";
import AddGroup from "./AddGroup";
import CreateGroup from "./CreateGroup";
import AddTypeUser from "./AddTypeUser";
import ChooseGroupOption from "./GroupeChoice";
import AddCSV from "./AddCSV";
import UserList from "./UserList";
import { uploadUsersByAdmin } from "../../../../store/api/users";
import { getAllGroups, addGroup } from "../../../../store/api/groups";

const UploadUsersModal = ({ open, onClose }) => {
  const queryClient = useQueryClient();

  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedTypeTitle, setSelectedTypeTitle] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(1);
  const [editingRow, setEditingRow] = useState(null);
  const [groupOption, setGroupOption] = useState(""); // "new" or "existing"
  const [availableGroups, setAvailableGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState(""); // For storing new group name
  const [groupError, setGroupError] = useState(""); // Added state for error message

  // Query to fetch all groups
  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ["allGroups"],
    queryFn: () =>
      getAllGroups({
        pageNumber: 1,
        sortBy: "name",
        isDescending: false,
        searchTerm: "",
      }),
  });
  // Mutation for creating a new group
  const { mutate: createGroupMutate, isLoading: isCreatingGroup } = useMutation(
    {
      mutationFn: (groupName) => addGroup(groupName),
      onSuccess: (data) => {
        // Set the newly created group's ID as the selected group
        setSelectedGroup(data);
        setGroupError(""); // Clear any previous errors
        queryClient.invalidateQueries("allGroups");
        handleNextStep(); // Move to next step after successful group creation
      },
      onError: (error) => {
        // Handle error
        console.error("Error creating group:", error);
        // Extract error message from API response
        if (error.response?.data?.title) {
          setGroupError(error.response.data.title);
        } else if (error.message) {
          setGroupError(error.message);
        } else {
          setGroupError("Failed to create group. Please try again.");
        }
      },
    }
  );

  useEffect(() => {
    if (!groupsLoading && groupsData && groupsData.paginatedContent) {
      setAvailableGroups(groupsData.paginatedContent);
    }
  }, [groupsLoading, groupsData]);

  const { mutate: uploadMutate } = useMutation({
    mutationFn: () =>
      uploadUsersByAdmin({
        users: csvData,
        userTypeId: selectedType,
        groupIds: [selectedGroup],
      }),
    onSuccess: (data) => {
      queryClient.refetchQueries("adminUsers");
      onClose();
    },
    onError: (error) => {
      // Handle error
      console.error("Error uploading users:", error);
    },
  });

  const handleGroupChange = (event) => {
    const { value } = event.target;
    setSelectedGroup(value);
  };

  const handleNewGroupNameChange = (event) => {
    setNewGroupName(event.target.value);
    // Clear error when user starts typing again
    if (groupError) {
      setGroupError("");
    }
  };

  const handleCreateNewGroup = () => {
    if (newGroupName.trim()) {
      createGroupMutate(newGroupName);
    }
  };

  const handleTypeChange = (event, data) => {
    const idType = event.target.value;
    setSelectedType(idType);
    setSelectedTypeTitle(data.find((el) => el.id === idType)?.title);
  };

  const handleDrop = (acceptedFiles) => {
    // Handle case when we're clearing the file (acceptedFiles is null)
    if (acceptedFiles === null) {
      setFile(null);
      setCsvData([]);
      return;
    }

    // Normal file upload handling
    if (acceptedFiles && acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);

      Papa.parse(uploadedFile, {
        header: true,
        complete: (result) => {
          setCsvData(result.data.filter((el) => el.fullName && el.email));
        },
      });
    }
  };

  const handleEditRow = (index) => {
    setEditingRow(index);
  };

  const handleSaveRow = (index, field, value) => {
    const updatedData = [...csvData];
    updatedData[index][field] = value;
    setCsvData(updatedData);
    setEditingRow(null);
  };

  const handleSubmit = () => {
    uploadMutate();
  };

  const handleNextStep = () => {
    setStep(step + 1);
  };

  const handleBackStep = () => {
    setStep(step - 1);
  };

  const handleGroupOptionSelect = (option) => {
    setGroupOption(option);
    setSelectedGroup(""); // Reset selected group when changing option
    setNewGroupName(""); // Reset new group name
    setGroupError(""); // Clear any errors
    setStep(2);
  };
  const handleDeleteRow = (index) => {
    const updatedData = [...csvData];
    setCsvData(updatedData.filter((_, i) => i !== index));
  };
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <ChooseGroupOption
            handleGroupOptionSelect={handleGroupOptionSelect}
            onClose={onClose}
          />
        );
      case 2:
        return groupOption === "new" ? (
          <CreateGroup
            groupName={newGroupName}
            handleGroupNameChange={handleNewGroupNameChange}
            handleCreateGroup={handleCreateNewGroup}
            handleBackStep={handleBackStep}
            onClose={onClose}
            isLoading={isCreatingGroup}
            error={groupError} // Pass error to component
          />
        ) : (
          <AddGroup
            selectedGroup={selectedGroup}
            handleGroupChange={handleGroupChange}
            handleNextStep={handleNextStep}
            handleBackStep={handleBackStep}
            onClose={onClose}
            groups={availableGroups}
          />
        );
      case 3:
        return (
          <AddTypeUser
            selectedType={selectedType}
            handleTypeChange={handleTypeChange}
            handleNextStep={handleNextStep}
            handleBackStep={handleBackStep}
            setSelectedTypeTitle={setSelectedTypeTitle}
          />
        );
      case 4:
        return (
          <AddCSV
            handleBackStep={handleBackStep}
            handleNextStep={handleNextStep}
            csvData={csvData}
            handleDrop={handleDrop}
            file={file}
          />
        );
      case 5:
        return (
          <UserList
            csvData={csvData}
            handleSaveRow={handleSaveRow}
            editingRow={editingRow}
            setEditingRow={setEditingRow}
            handleEditRow={handleEditRow}
            handleBackStep={handleBackStep}
            handleSubmit={handleSubmit}
            selectedTypeTitle={selectedTypeTitle}
            selectedGroupName={
              groupsData?.paginatedContent?.find(
                (el) => el.id === selectedGroup
              )?.name
            }
            handleDeleteRow={handleDeleteRow}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div
        className="modal-content"
        style={{
          padding: "20px",
          backgroundColor: "white",
          margin: "1% auto",
          width: "600px",
          minWidth: "400px",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
        }}
      >
        <div style={headerStyle}>
          <Typography
            variant="h6"
            component="h2"
            style={{ fontWeight: "bold" }}
          >
            Add users
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>

        <div style={contentStyle}>{renderStepContent()}</div>
      </div>
    </Modal>
  );
};

export default UploadUsersModal;

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
};

const contentStyle = {
  width: "100%", // Ensure content takes full width of modal
  minHeight: "300px", // Minimum height for content area
};
